import type {
  CompanyPolicyDetailDTO,
  CreateCompanyPolicyDTO,
  CreatePolicyTypeDTO,
  ListCompanyPoliciesQuery,
  ListCompanyPoliciesResult,
  ListPolicyVersionsQuery,
  ListPolicyVersionsResult,
  PolicyType,
  UpdateCompanyPolicyDTO,
  UpdateCompanyPolicyStatusDTO,
  UpdatePolicyTypeDTO,
} from "@/modules/hr/company-policy/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

const base = `${config.apiUrl}/hr/company-policy`;

function buildQueryString(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") search.set(k, String(v));
  });
  const q = search.toString();
  return q ? `?${q}` : "";
}

export const companyPolicyApi = {
  list(query: ListCompanyPoliciesQuery): Promise<ListCompanyPoliciesResult> {
    return fetcher.get<ListCompanyPoliciesResult>(
      `${base}${buildQueryString({
        offset: query.offset,
        limit: query.limit,
        sort: query.sort ? JSON.stringify(query.sort) : undefined,
        policyTypeId: query.policyTypeId,
        status: query.status,
        search: query.search,
      })}`,
    );
  },

  listPolicyTypes(): Promise<PolicyType[]> {
    return fetcher.get<PolicyType[]>(`${base}/policy-types`);
  },

  createPolicyType(dto: CreatePolicyTypeDTO): Promise<PolicyType> {
    return fetcher.post<PolicyType>(`${base}/policy-types`, dto);
  },

  updatePolicyType(id: string, dto: UpdatePolicyTypeDTO): Promise<PolicyType> {
    return fetcher.patch<PolicyType>(`${base}/policy-types/${id}`, dto);
  },

  deletePolicyType(id: string): Promise<{ ok: true }> {
    return fetcher.delete<{ ok: true }>(`${base}/policy-types/${id}`);
  },

  getById(id: string): Promise<CompanyPolicyDetailDTO> {
    return fetcher.get<CompanyPolicyDetailDTO>(`${base}/${id}`);
  },

  create(dto: CreateCompanyPolicyDTO): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(base, dto);
  },

  update(id: string, dto: UpdateCompanyPolicyDTO): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(`${base}/${id}`, dto);
  },

  updateStatus(id: string, dto: UpdateCompanyPolicyStatusDTO): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(`${base}/${id}/status`, dto);
  },

  delete(id: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(`${base}/${id}`);
  },

  listVersions(query: ListPolicyVersionsQuery): Promise<ListPolicyVersionsResult> {
    const { policyId, ...rest } = query;
    return fetcher.get<ListPolicyVersionsResult>(
      `${base}/${policyId}/versions${buildQueryString(rest)}`,
    );
  },
};
